import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Users, Search, Building2, DoorOpen, ArrowRight } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROOM_COLORS = ["#FFD60A", "#FF6B2B", "#2DC653", "#0A6E6E"];
const OG_IMAGE = "https://customer-assets.emergentagent.com/job_book-spaces-2/artifacts/2dhwnmm0_therig.png";

export default function Spaces() {
  const navigate = useNavigate();
  const [spaces, setSpaces] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Our Spaces - Meeting Rooms & Offices | The Rig";
    axios.get(`${API}/spaces`).then(r => { setSpaces(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const filtered = spaces.filter(s => {
    if (filter !== "all" && s.type !== filter) return false;
    if (search && !s.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="min-h-screen" data-testid="spaces-page">
      <title>Our Spaces - Meeting Rooms & Offices | The Rig</title>
      <meta name="description" content="Browse and book flexible meeting rooms and office spaces at The Rig. Multiple layouts, hourly/daily/monthly rates. Boardroom, classroom, theater and U-shape configurations." />
      <meta property="og:title" content="Our Spaces - Meeting Rooms & Offices | The Rig" />
      <meta property="og:description" content="Browse and book flexible meeting rooms and office spaces at The Rig. Multiple layouts, hourly/daily/monthly rates." />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={OG_IMAGE} />
      {/* Hero header */}
      <section className="bg-rig-charcoal py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6">
          <span className="inline-block bg-rig-orange/20 text-rig-orange text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-full mb-4">
            Available Now
          </span>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl text-white mb-3" data-testid="spaces-title">Our Spaces</h1>
          <p className="text-white/50 text-base md:text-lg max-w-lg">Find and book the perfect workspace for your needs</p>
        </div>
      </section>

      {/* Multi-color stripe */}
      <div className="flex h-1">
        <div className="flex-1" style={{ backgroundColor: "#FFD60A" }} />
        <div className="flex-1" style={{ backgroundColor: "#FF6B2B" }} />
        <div className="flex-1" style={{ backgroundColor: "#2DC653" }} />
        <div className="flex-1" style={{ backgroundColor: "#0A6E6E" }} />
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-10" data-testid="spaces-filters">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-rig-charcoal/40" />
            <Input
              placeholder="Search spaces..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-10 rounded-full border-gray-200"
              data-testid="spaces-search-input"
            />
          </div>
          <div className="flex gap-2">
            {[
              { value: "all", label: "All", icon: null },
              { value: "meeting_room", label: "Meeting Rooms", icon: DoorOpen },
              { value: "office", label: "Offices", icon: Building2 },
            ].map(f => (
              <Button
                key={f.value}
                variant={filter === f.value ? "default" : "outline"}
                className={filter === f.value
                  ? "bg-rig-charcoal text-white rounded-full"
                  : "border-gray-200 rounded-full text-rig-charcoal/60 hover:text-rig-charcoal"
                }
                onClick={() => setFilter(f.value)}
                data-testid={`filter-${f.value}`}
              >
                {f.icon && <f.icon className="h-4 w-4 mr-1" />}
                {f.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-80 rounded-2xl bg-gray-100 animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-rig-charcoal/40" data-testid="no-spaces-message">
            <Building2 className="h-12 w-12 mx-auto mb-4 opacity-40" />
            <p className="text-lg font-heading font-bold">No spaces found</p>
            <p className="text-sm">Try adjusting your filters or search</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="spaces-grid">
            {filtered.map((space, i) => {
              const color = ROOM_COLORS[i % ROOM_COLORS.length];
              return (
                <div
                  key={space.id}
                  className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer group"
                  style={{ boxShadow: `0 4px 20px ${color}12` }}
                  onClick={() => navigate(`/spaces/${space.id}`)}
                  data-testid={`space-card-${i}`}
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={space.photos?.[0] || ""}
                      alt={space.name}
                      className="w-full h-52 object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-25 transition-opacity duration-300" style={{ backgroundColor: color }} />
                    <span className="absolute top-3 right-3 text-xs font-bold px-3 py-1 rounded-full text-white" style={{ backgroundColor: color }}>
                      <Users className="h-3 w-3 inline mr-1" />{space.capacity} seats
                    </span>
                    {/* Rented Out banner */}
                    {space.occupancy?.is_rented_out && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <span className="bg-rig-charcoal/90 text-white text-sm font-bold px-5 py-2 rounded-full tracking-wider uppercase shadow-lg border border-white/20">
                          Rented Out
                        </span>
                      </div>
                    )}
                    {!space.guest_allowed && !space.occupancy?.is_rented_out && (
                      <Badge className="absolute top-3 left-3 bg-rig-charcoal text-white rounded-full text-xs">Members Only</Badge>
                    )}
                  </div>
                  <div className="p-5">
                    <p className="text-xs text-rig-charcoal/40 uppercase tracking-wider font-medium mb-1">
                      {space.type === "meeting_room" ? "Meeting Room" : "Office"}
                    </p>
                    <h3 className="font-heading font-bold text-lg text-rig-charcoal mb-2 group-hover:text-rig-orange transition-colors">{space.name}</h3>
                    <p className="text-sm text-rig-charcoal/50 line-clamp-2 mb-4">{space.description}</p>
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <div className="flex items-center gap-3 text-sm">
                        {space.occupancy?.is_rented_out ? (
                          <span className="font-heading font-semibold text-sm text-rig-charcoal/40 italic">Currently Occupied</span>
                        ) : space.type === "office" ? (
                          <a href="/contact" onClick={e => { e.stopPropagation(); navigate("/contact"); }} className="font-heading font-semibold text-sm text-rig-orange hover:underline">Contact for pricing</a>
                        ) : (
                          <span className="font-heading font-extrabold text-lg" style={{ color }}>XCG {space.pricing?.hourly}<span className="text-sm font-normal text-rig-charcoal/40">/hr</span></span>
                        )}
                      </div>
                      {!space.occupancy?.is_rented_out && space.type !== "office" && (
                        <span className="text-sm font-bold text-rig-orange group-hover:translate-x-1 transition-transform inline-flex items-center" data-testid={`book-space-${i}`}>
                          Book <ArrowRight className="ml-1 h-3.5 w-3.5" />
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
